import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * The last line of defence.
 *
 * Without one of these, a single throw anywhere in the tree unmounts everything
 * and React renders an empty page. That is exactly what happened when
 * `useQuery` called Dexie 3's `db.on('changes')` on Dexie 4: the floor planner
 * went white, with nothing on screen saying why.
 *
 * On a wall tablet a white screen is the worst possible failure. Nobody is
 * standing in front of it to open a console, and the board is supposed to be
 * the thing you glance at to know the office is fine. So a crash here shows
 * something legible and offers the two things that actually help: try again,
 * and reload.
 */
interface Props {
  children: ReactNode
  /** Names the area, so the message can say WHICH part failed. */
  area?: string
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep it in the console for whoever does eventually look, and let a host
    // (the native shell, a logger) hear about it.
    console.error('Office OS crashed:', error, info.componentStack)
    this.props.onError?.(error, info)
  }

  private readonly retry = (): void => this.setState({ error: null })

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    const where = this.props.area ?? 'this screen'
    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          background: 'var(--bg)',
          color: 'var(--text)',
          fontFamily: 'var(--font)',
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: '100%',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow)',
            padding: '24px 26px',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong in {where}</h1>
          <p style={{ color: 'var(--text-muted)', margin: '0 0 6px' }}>
            The rest of the office is unaffected, and nothing you saved has been lost —
            every change is stored on this device until it syncs.
          </p>
          <p
            style={{
              color: 'var(--text-subtle)',
              fontFamily: 'var(--mono)',
              fontSize: 13,
              margin: '0 0 18px',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={this.retry}>Try again</button>
            <button className="btn" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      </div>
    )
  }
}
